import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  ExportMenuItems,
  FormControlSizes,
  IconName,
  MixpanelEvent,
  QUOTE,
  Quote,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useMixpanel,
  usePersistedBlotterTable,
  useQuoteColumns,
  useWsBlotterTable,
  type Column,
} from '@talos/kyoko';
import { pick } from 'lodash-es';
import { useMemo } from 'react';

import type { RowDoubleClickedEvent } from 'ag-grid-community';
import { useQuotesFilter, type QuotesServerFilter, type QuotesTableFilter } from './useQuotesFilter';
import { useQuotesMenu } from './useQuotesMenu';

export interface FilteredQuotesParams {
  blotterID: string;
  defaultColumns?: Column[];
  defaultFilter?: QuotesTableFilter;
  initialIsOpen?: boolean;

  onCloneTab?: () => void;
  onRowDoubleClicked?: (event: RowDoubleClickedEvent<Quote>) => void;
}

export function QuotesBlotterTable({
  blotterID,
  defaultColumns,
  defaultFilter,
  initialIsOpen,
  onCloneTab,
  onRowDoubleClicked,
}: FilteredQuotesParams) {
  const mixpanel = useMixpanel();

  const columns = useQuoteColumns({ defaultColumns });

  const persistedBlotterTable = usePersistedBlotterTable(blotterID, {
    filter: defaultFilter,
  });

  const filterResults = useQuotesFilter({
    initialFilter: persistedBlotterTable.initialState?.filter,
    onFilterChanged: persistedBlotterTable.onFilterChanged,
  });
  const { blotterFilterFn: clientLocalFilter, blotterTableFilterProps, filterBuilderProps } = filterResults;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });

  const quotesMenu = useQuotesMenu({
    openClause: filterBuilderAccordion.openClause,
    filterableProperties: filterBuilderProps.properties,
  });

  const columnsWithMenu = useMemo(() => [...columns, ...quotesMenu.columns], [columns, quotesMenu.columns]);

  const request = useMemo(
    () => ({
      tag: blotterID,
      name: QUOTE,
      ...removeEmptyFilters(quotesServerFilter(filterResults.filter)),
    }),
    [blotterID, filterResults.filter]
  );

  const blotterTable = useWsBlotterTable({
    request,
    rowID: Quote.rowID,
    clientLocalFilter,
    persistence: persistedBlotterTable,
    columns: columnsWithMenu,
    gridOptions: {
      onRowDoubleClicked,
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      getContextMenuItems: quotesMenu.getContextMenuItems,
    },
    exportOptions: {
      name: 'Quotes',
    },
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const handleCloneTab = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.CloneTab);
    onCloneTab?.();
    extrasMenuPopover.close();
  });

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTableFilterProps}
        {...blotterTable.blotterTableFiltersProps}
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />

            {onCloneTab && (
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
              >
                Clone Tab
              </Button>
            )}
          </BlotterTableExtrasMenu>
        }
      />
      <BlotterTable {...blotterTable} />
      {quotesMenu.dialogs}
    </>
  );
}

export function quotesServerFilter(filter: QuotesTableFilter | undefined): QuotesServerFilter | undefined {
  if (!filter) {
    return undefined;
  }

  const serverFilter: QuotesServerFilter = pick(filter, [
    'StartDate',
    'EndDate',
    'Users',
    'Statuses',
    'Symbols',
    'RFQID',
    'ParentRFQID',
  ]);
  return serverFilter;
}
